/**
 * Where a signed-out kiosk browser is allowed to be.
 *
 * A machine that holds a durable screen credential but has no Clerk session is
 * ambiguous: it is either a venue kiosk that just booted (belongs on /player,
 * playing) or a human who wants back into the control app. The router cannot
 * tell them apart, so it used to resolve the ambiguity by bouncing EVERY route
 * to /player — which trapped the human completely. With no session there is no
 * signed-in UI to sign out from, and the only escape was clearing localStorage
 * by hand.
 *
 * The fix is to stop treating "reach a page" as the thing worth guarding. What
 * actually protects a venue is the exit PIN on RELEASING the device, and that
 * is untouched by any of this: opening the sign-in page does not release the
 * credential, does not stop playback, and does not reveal anything. So the auth
 * routes stay reachable always, and a kiosk still boots straight into playback.
 */

/**
 * Routes a kiosk browser is never bounced away from. Clerk appends sub-paths
 * for multi-factor and SSO callbacks (/sign-in/factor-one, /sign-up/continue),
 * so these match by prefix rather than equality — a bounce mid-2FA would be the
 * same trap wearing a different hat.
 */
const ALWAYS_REACHABLE = ["/sign-in", "/sign-up"];

/** The player surface itself, where an unattended kiosk belongs. */
export const KIOSK_HOME = "/player";

export function isAlwaysReachable(location: string): boolean {
  const path = location.split(/[?#]/, 1)[0];
  return ALWAYS_REACHABLE.some(
    (route) => path === route || path.startsWith(`${route}/`),
  );
}

/**
 * Whether this navigation should be redirected back to the player surface.
 *
 * Order matters: a signed-in operator is always free (staff may run the control
 * app on a machine that is also a player), and an operator who deliberately
 * signed out this session has already declared intent.
 */
export function shouldReturnToKiosk(opts: {
  /** A durable screen credential is stored on this machine. */
  isKioskPlayer: boolean;
  /** A Clerk session is active. */
  isSignedIn: boolean;
  /** The operator explicitly signed out during this browsing session. */
  operatorExited: boolean;
  /** Current wouter location, may carry a query string or hash. */
  location: string;
}): boolean {
  if (!opts.isKioskPlayer) return false;
  if (opts.isSignedIn) return false;
  if (opts.operatorExited) return false;
  if (isAlwaysReachable(opts.location)) return false;
  return opts.location !== KIOSK_HOME;
}
